// Counter Intelligence — client-side page context.
//
// What the drawer tells the server about WHERE the user is asking from. The
// server does the describing (supabase/functions/_shared/pageContext.mjs owns
// the page registry); this module only decides what to send, and one thing the
// server can't: whether an answer should carry an action offer.
//
// Pure (no React / no I/O beyond reading `window`) so it is unit-testable.

// The surface string: the pathname without its leading slash ("items.html",
// "add-items.html"). The server looks this up in the page registry, so it is the
// whole basis of the assistant's page awareness.
export function surfaceForApi(loc) {
  const pathname = (loc || (typeof window !== 'undefined' ? window.location : null))?.pathname || '';
  return pathname.replace(/^\//, '') || 'app';
}

// Phone vs desktop. The two have different navigation — there is no sidebar on a
// phone, and everything past Home/Counts/Orders/Reports lives behind "More" — so
// a path-giving answer is wrong for half the users unless the answerer knows
// which one it is talking to. 768px is the breakpoint the rest of the app uses
// (InventoryManager, mobile-nav.js); the Expo shell is always a phone.
export function viewportForApi(win) {
  const w = win || (typeof window !== 'undefined' ? window : null);
  if (!w) return 'desktop';
  try {
    if (w.ReactNativeWebView) return 'mobile';
    if (typeof w.matchMedia === 'function' && w.matchMedia('(max-width: 768px)').matches) return 'mobile';
  } catch (_) { /* fall through — desktop is the safe default */ }
  return 'desktop';
}

// Which help-topic ids are genuinely ABOUT an action the chat can perform. A ⓘ
// ask is a request to understand ONE control, not to start a task, so an offer
// is only appended when the control the user tapped is itself about that action.
//
// Keyed by the support intent whose answer carries an offer (see the drawer's
// answerSupport). Anything not listed here never suppresses an offer.
const OFFER_TOPICS = {
  items: /import|manual_entry|build_from_menu/,
  prices: /price/,
  duplicates: /duplicate|relationship/,
};

/**
 * Should the answer for `intent` be followed by its in-chat action offer?
 *
 * A question TYPED into the chat box always gets the offer (helpTopicId is null)
 * — that is the behaviour that turns "how do I add my items?" into an actual
 * import. A ⓘ ask only gets it when the control is about that action, because
 * appending "I can read PDFs, Excel and CSV spreadsheets…" to an explanation of
 * what an inventory item IS answers a question nobody asked.
 */
export function shouldOfferAction(intent, helpTopicId) {
  if (!helpTopicId) return true;
  const pattern = OFFER_TOPICS[intent];
  if (!pattern) return true;
  return pattern.test(String(helpTopicId));
}
